import { useEffect, useRef, useState } from 'react';
import Raven from 'raven-js';
import enviro from 'enviro';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { getFullUrl } from 'hubspot-url-utils';
import PortalIdParser from 'PortalIdParser';
import { CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Pictogram } from 'foundations-components/transitional/components/Pictogram';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { useCreatePortalForAcademyLabMutation } from '../../../../__generated__/chirp/com/hubspot/academy/simulation/environment/rpc/PortalService.hooks';
import UsageTracker from '../../../utils/UsageTracker';
import { Metrics } from '../../../Metrics';
import { ACADEMY_LABS_SOURCE_PORTAL_ID, ACADEMY_LABS_SOURCE_PORTAL_ID_QA } from '../../accounts/accountsConstants';
import { ACADEMY_LABS_SESSION_URL_FRAGMENT } from '../../../constants/Navigation';
import { redirectTo } from '../../../utils/urls';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SOURCE_PORTAL_ID = enviro.isProd() ? ACADEMY_LABS_SOURCE_PORTAL_ID : ACADEMY_LABS_SOURCE_PORTAL_ID_QA;
const getErrorMessageKey = error => {
  const networkError = error.networkError;
  const cause = networkError === null || networkError === void 0 ? void 0 : networkError.cause;
  if ((cause === null || cause === void 0 ? void 0 : cause.chirpErrorType) === 'NOT_AUTHORIZED') {
    return 'learningCenter.academyLabs.createPortalNotAuthorizedError';
  }
  if (cause !== null && cause !== void 0 && cause.chirpErrorMessage) {
    return 'learningCenter.academyLabs.createPortalIssueError';
  }
  return 'learningCenter.academyLabs.createPortalError';
};
const getErrorType = error => {
  var _error$networkError;
  const cause = (_error$networkError = error.networkError) === null || _error$networkError === void 0 ? void 0 : _error$networkError.cause;
  if ((cause === null || cause === void 0 ? void 0 : cause.chirpErrorType) === 'NOT_AUTHORIZED') {
    return 'not_authorized';
  }
  if (cause !== null && cause !== void 0 && cause.chirpErrorMessage) {
    return 'chirp_error';
  }
  return 'unknown';
};
const PORTAL_SETUP_DELAY_MS = 30000;
const NEWLY_CREATED_THRESHOLD_MS = 10000;
const AcademyLabsLandingPage = () => {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const redirectTimeoutRef = useRef(0);
  useEffect(() => {
    return () => clearTimeout(redirectTimeoutRef.current);
  }, []);
  const handleCreatePortalError = error => {
    Metrics.counter('academy-labs-portal-access-error', {
      error_type: getErrorType(error)
    }).increment();
    FloatingAlertStore.addAlert({
      type: 'danger',
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: getErrorMessageKey(error)
      })
    });
    Raven.captureException(error);
  };
  const handleNoPortalError = () => {
    Metrics.counter('academy-labs-portal-access-error', {
      error_type: 'no_portal_id'
    }).increment();
    FloatingAlertStore.addAlert({
      type: 'danger',
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.academyLabs.createPortalError"
      })
    });
    Raven.captureException(new Error('createPortalForAcademyLab succeeded but returned no portalId'));
  };
  const handleCreatePortalCompleted = data => {
    const {
      portalId,
      createdAt
    } = data.result;
    if (portalId) {
      const originPortalId = PortalIdParser.get();
      const sessionUrl = `${getFullUrl('app')}/academy/${portalId}${ACADEMY_LABS_SESSION_URL_FRAGMENT}?originPortalId=${originPortalId}`;
      const isNewPortal = Date.now() - createdAt < NEWLY_CREATED_THRESHOLD_MS;
      Metrics.counter('academy-labs-portal-access-success').increment();
      if (isNewPortal) {
        Metrics.counter('academy-labs-portal-new-provisioned').increment();
      } else {
        Metrics.counter('academy-labs-portal-existing-returned').increment();
      }
      const delay = isNewPortal ? PORTAL_SETUP_DELAY_MS : 0;
      setIsRedirecting(isNewPortal);
      redirectTimeoutRef.current = window.setTimeout(() => {
        redirectTo(sessionUrl);
      }, delay);
    } else {
      handleNoPortalError();
    }
  };
  const [createPortalForAcademyLab, {
    loading
  }] = useCreatePortalForAcademyLabMutation({
    onCompleted: handleCreatePortalCompleted,
    onError: handleCreatePortalError
  });
  const onClickPracticeModeBtn = () => {
    UsageTracker.trackStandalone('Learning Center Interaction', {
      action: 'academy-labs-enter-practice-mode-clicked'
    });
    Metrics.counter('academy-labs-portal-requested').increment();
    void createPortalForAcademyLab({
      variables: {
        exerciseId: 0,
        sourcePortalId: SOURCE_PORTAL_ID
      }
    });
  };
  return /*#__PURE__*/_jsx(View, {
    marginInline: "auto",
    maxInlineSize: "900px",
    paddingBlockStart: "1100",
    children: /*#__PURE__*/_jsx(CardWrapper, {
      children: /*#__PURE__*/_jsx(CardSection, {
        children: /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          flexDirection: "column",
          paddingBlock: "700",
          children: [/*#__PURE__*/_jsxs(View, {
            marginBlockEnd: "700",
            marginBlockStart: "500",
            textAlign: "center",
            children: [/*#__PURE__*/_jsx(Text, {
              marginBlockEnd: "300",
              typography: "heading-700",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.academyLabs.heading"
              })
            }), /*#__PURE__*/_jsx(Text, {
              marginBlockEnd: "500",
              typography: "heading-500",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.academyLabs.description"
              })
            })]
          }), /*#__PURE__*/_jsx(Pictogram, {
            name: "Playbooks",
            size: "md"
          }), /*#__PURE__*/_jsx(LoadingButton, {
            loading: loading || isRedirecting,
            marginBlockStart: "1100",
            onClick: onClickPracticeModeBtn,
            use: "primary",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.academyLabs.enterButton"
            })
          }), isRedirecting && /*#__PURE__*/_jsx(Text, {
            marginBlockStart: "300",
            typography: "body-100",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.academyLabs.preparingPortal"
            })
          })]
        })
      })
    })
  });
};
export default AcademyLabsLandingPage;